import React, { useState } from 'react'
import moment from 'moment'
import { Button } from '@mui/material'
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined'
import useWebTicketList from './services/hooks/useWebTicketList'
import useMarkWebTicketRead from './services/hooks/useMarkWebTicketRead'
import Loader from '@/components/Loader'
import CustomPageNavigator from '@/components/Pagination'
import WebContactFormDetailsModel from './component/WebDetailModal'
import { WUserInterface } from './services/types/APIResponseType'
import DataUnavailable from '@/components/DataUnavailable'
import { invalidateQuery } from '@/services/config/queryClient'

const WebContactForm: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [open, setOpen] = React.useState<boolean>(false)
  const [userInfo, setUserInfo] = React.useState<WUserInterface | null>(null)
  const itemsPerPage = 5

  const { data: webTicketList, isLoading } = useWebTicketList(
    currentPage,
    itemsPerPage,
  )
  const { mutate: markWebTicketRead } = useMarkWebTicketRead()

  const handleClose = () => setOpen(false)

  const handleView = (item: WUserInterface) => {
    setUserInfo(item)
    setOpen(true)

    // Mark unread submissions as read so the action icon reflects that it
    // has been dealt with, then refresh the list to show the new state.
    if (!item.isRead) {
      markWebTicketRead(
        { contactId: item._id },
        {
          onSuccess: () => invalidateQuery(['webTicketList']),
        },
      )
    }
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <div>
      {webTicketList && webTicketList?.data?.contactUs.length > 0 ? (
        <>
          {open && (
            <WebContactFormDetailsModel
              onClose={handleClose}
              open={open}
              userDetails={userInfo}
            />
          )}
          <div className="w-full overflow-auto">
            <table className="table-container bg-grad before:rounded-[20px] rounded-[20px]">
              <thead>
                <tr className="table-heading row-bg1">
                  <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                    Name
                  </th>
                  <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                    Email Address
                  </th>
                  <th className="text-left whitespace-pre border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                    Date & Time
                  </th>
                  <th className="text-left whitespace-pre">Action</th>
                </tr>
              </thead>
              <tbody>
                {webTicketList &&
                  webTicketList?.data?.contactUs.map(
                    (item: WUserInterface, idx: number) => (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? 'rowbg2' : 'row-bg1'}
                      >
                        <td className="border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                          <div className="profile-cell text-left">
                            {[item?.firstName, item?.lastName]
                              .filter(Boolean)
                              .join(' ') || 'N/A'}
                          </div>
                        </td>
                        <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                          {item?.email || 'N/A'}
                        </td>
                        <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                          {item?.updatedAt
                            ? moment(item?.updatedAt).format(
                                'YYYY/MM/DD | h:mm',
                              )
                            : 'N/A'}
                        </td>
                        <td>
                          <Button
                            onClick={() => handleView(item)}
                            title={
                              item?.isRead ? 'Read' : 'Unread — click to view'
                            }
                          >
                            <VisibilityOutlined
                              sx={{
                                color: item?.isRead ? '#d32f2f' : '#ffffff',
                              }}
                            />
                          </Button>
                        </td>
                      </tr>
                    ),
                  )}
              </tbody>
            </table>
          </div>
          {webTicketList && (
            <CustomPageNavigator
              currentPage={currentPage}
              totalItemCount={webTicketList?.data?.count}
              limitPerPage={itemsPerPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </>
      ) : (
        <DataUnavailable description="No record found" />
      )}
    </div>
  )
}

export default WebContactForm
